<script setup lang="ts">
/**
 * One note opened (#690: "selecting a node shows its evidence, timestamps, provenance, labels and correction state"):
 * its kind and whether it is in use, what it says, and — for a correction — what it said before it evolved. Then
 * where it came from, when, the labels it is filed under (organisation-wide, never private), and how it relates to
 * the notes around it, in words. The page's own controls (Ask, Train, Correct) come through `actions`; `drawn` marks
 * them as a picture of the portal rather than the portal itself.
 *
 * A screen with sentences of its own says the kind, the state and each relationship in them (`typeWords`,
 * `stateWords`, `relationWords`; anything they do not name keeps the kit's word), and adds facts the kit does not
 * know about (where a note is stored, what to say when it has no label) through `facts`: `<div><dt>…</dt><dd>…</dd>
 * </div>` pairs, drawn after the kit's own and in the same voice.
 */
import { computed } from 'vue'
import { otherEnd } from '../lib/knowledge'
import type { KnowledgeRelation, KnowledgeState, KnowledgeType } from '../lib/surfaces'
import {
    KNOWLEDGE_RELATION_WORD,
    KNOWLEDGE_STATE_WORD,
    KNOWLEDGE_TYPE_WORD,
    type KnowledgeEdgeView,
    type KnowledgeNodeView,
} from '../lib/surfaces'

const props = defineProps<{
    node: KnowledgeNodeView
    /** Every note, to name the ones this one relates to. */
    nodes: readonly KnowledgeNodeView[]
    edges: readonly KnowledgeEdgeView[]
    drawn?: boolean
    /** The words to say each kind of note in, over the kit's own. */
    typeWords?: Partial<Record<KnowledgeType, string>>
    /** The words to say each state in, over the kit's own. */
    stateWords?: Partial<Record<KnowledgeState, string>>
    /** The words to say each relationship in, over the kit's own. */
    relationWords?: Partial<Record<KnowledgeRelation, string>>
}>()

const typeWord = computed(
    () => props.typeWords?.[props.node.knowledgeType] ?? KNOWLEDGE_TYPE_WORD[props.node.knowledgeType],
)
const stateWord = computed(
    () => props.stateWords?.[props.node.correctionState] ?? KNOWLEDGE_STATE_WORD[props.node.correctionState],
)

const byId = computed(() => new Map(props.nodes.map(node => [node.id, node])))

/** What this correction replaced: the note it supersedes. */
const replaced = computed(() =>
    props.edges
        .filter(edge => edge.type === 'supersedes' && edge.source === props.node.id)
        .flatMap(edge => byId.value.get(edge.target) ?? []),
)

/** Every other relationship, in words, with the note at its other end. */
const relations = computed(() =>
    props.edges.flatMap(edge => {
        if (edge.type === 'supersedes' && edge.source === props.node.id) return []
        const otherId = otherEnd(edge, props.node.id)
        const other = otherId ? byId.value.get(otherId) : undefined
        if (!other) return []
        const word =
            edge.type === 'supersedes'
                ? 'Superseded by'
                : (props.relationWords?.[edge.type] ?? KNOWLEDGE_RELATION_WORD[edge.type])
        return [{ id: edge.id, type: edge.type, word, other }]
    }),
)
</script>

<template>
    <article class="zh-kd" :data-type="node.knowledgeType" :data-state="node.correctionState">
        <p class="zh-kd__top">
            <span class="zh-kd__type">{{ typeWord }}</span>
            <span class="zh-kd__state">{{ stateWord }}</span>
        </p>
        <p class="zh-kd__body">{{ node.body }}</p>
        <p v-for="old in replaced" :key="old.id" class="zh-kd__was">
            <span class="zh-kd__label">Replaces</span> <s>{{ old.body }}</s>
        </p>

        <dl class="zh-kd__facts">
            <div v-if="node.provenance" class="zh-kd__fact">
                <dt class="zh-kd__label">Evidence</dt>
                <dd>{{ node.provenance }}</dd>
            </div>
            <div v-if="node.recorded" class="zh-kd__fact">
                <dt class="zh-kd__label">Recorded</dt>
                <dd>{{ node.recorded }}</dd>
            </div>
            <div v-if="node.labels.length" class="zh-kd__fact">
                <dt class="zh-kd__label">Labels</dt>
                <dd class="zh-kd__labels">
                    <span v-for="label in node.labels" :key="`${label.kind}-${label.name}`" class="zh-kd__chip">{{
                        label.name
                    }}</span>
                </dd>
            </div>
            <slot name="facts" :node="node" />
        </dl>

        <section v-if="relations.length" class="zh-kd__relations" aria-label="Related notes">
            <p class="zh-kd__label">Related</p>
            <ul>
                <li v-for="relation in relations" :key="relation.id" :data-relation="relation.type">
                    <span class="zh-kd__rel">{{ relation.word }}<span class="zh-kd__sr">: </span></span>
                    <span :class="{ 'zh-kd__struck': relation.other.correctionState === 'corrected' }">{{
                        relation.other.body
                    }}</span>
                </li>
            </ul>
        </section>

        <div v-if="$slots.actions" class="zh-kd__actions" :aria-hidden="drawn ? 'true' : undefined">
            <slot name="actions" :node="node" />
        </div>
    </article>
</template>

<style scoped>
p,
dl,
dd,
ul {
    margin: 0;
}

ul {
    padding: 0;
    list-style: none;
}

.zh-kd {
    display: grid;
    align-content: start;
    gap: 0.875rem;
    padding: 1.125rem 1.25rem;
    border: 1px solid color-mix(in srgb, var(--electric-cyan) 45%, transparent);
    background: var(--void-black);
}

/* The system label (BG §19.3). */
.zh-kd__label,
.zh-kd__type,
.zh-kd__state,
.zh-kd__rel {
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-kd__top {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 0.5rem;
}

.zh-kd__type {
    color: var(--electric-cyan);
}

.zh-kd__body {
    color: var(--ink-1);
    font-size: 1.0625rem;
    font-weight: 600;
    line-height: 1.35;
}

[data-state="corrected"] .zh-kd__body {
    color: var(--ink-3);
    text-decoration: line-through;
}

.zh-kd__was {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 0.5rem;
    align-items: baseline;
    color: var(--ink-3);
    font-size: 0.875rem;
}

.zh-kd__was s,
.zh-kd__struck {
    text-decoration: line-through;
    text-decoration-color: var(--line);
}

.zh-kd__facts {
    display: grid;
    gap: 0.625rem;
    padding-top: 0.875rem;
    border-top: 1px solid var(--hair);
}

.zh-kd__fact {
    display: grid;
    gap: 0.125rem;
}

.zh-kd__fact dd {
    color: var(--ink-1);
    font-size: 0.875rem;
    line-height: 1.4;
}

/* A screen's own facts, in the kit's voice: each pair a fact, its term the system label. */
.zh-kd__facts > :slotted(div) {
    display: grid;
    gap: 0.125rem;
}

.zh-kd__facts :slotted(dt) {
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-kd__facts :slotted(dd) {
    margin: 0;
    color: var(--ink-1);
    font-size: 0.875rem;
    line-height: 1.4;
}

.zh-kd__labels {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
}

.zh-kd__chip {
    padding: 0.0625rem 0.4375rem;
    border: 1px solid var(--line);
    color: var(--ink-2);
    font-size: 0.8125rem;
}

.zh-kd__relations {
    display: grid;
    gap: 0.5rem;
    padding-top: 0.875rem;
    border-top: 1px solid var(--hair);
}

.zh-kd__relations ul {
    display: grid;
    gap: 0.5rem;
}

.zh-kd__relations li {
    display: grid;
    gap: 0.125rem;
    color: var(--ink-2);
    font-size: 0.875rem;
    line-height: 1.4;
}

/* Heard, not seen: the word and the note it names sit on two lines, so a reader hears the break as a colon. */
.zh-kd__sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
}

.zh-kd__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-top: 0.875rem;
    border-top: 1px solid var(--hair);
}
</style>
